import { useGetGuidParam } from '@risksmart-app/components/src/routes/routes.utils';
import {
  Assessment_Activity_Status_Enum,
  Assessment_Activity_Type_Enum,
  Parent_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import type { FC } from 'react';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { PageWrapper } from 'src/components/form/form/PageWrapper';
import AssessmentActivityForm from 'src/pages/assessments/forms/assessment-activity-form';
import type { AssessmentActivityFormDataFields } from 'src/pages/assessments/forms/assessment-activity-form/assessmentActivitySchema';

import { useInsertAssessmentActivityWithLinkedItems } from '@/hooks/mutations/assessment-activity/useInsertAssessmentActivityWithLinkedItems';
import { useFileUpdate } from '@/hooks/mutations/file/useFileUpdate';
import { MUTATION_NO_OP } from '@/hooks/useMutationResultNotification';
import {
  type CreatedEntityDestination,
  CreatedEntityFileSaveError,
  saveFilesForOptionalCreatedEntity,
} from '@/utils/createWithFiles';

import type { AssessmentTypeEnum } from '../../../../types';
import { useAssessmentTypeConfig } from '../../../../useAssessmentTypeConfig';

interface Props {
  assessmentMode: AssessmentTypeEnum;
}

const createAttemptSnapshot = (
  variables: AssessmentActivityFormDataFields,
  assessmentId: string
) =>
  cloneDeep({
    assessmentId,
    ...variables,
    // `files` is a permitted condition target, and FormContext nulls every
    // hidden field on submit, so a conditional attach-files control arrives
    // here as null rather than []. Reading it unguarded threw before the
    // insert was ever issued, failing the save outright (COI-530).
    files: (variables.files ?? []).map((file) => {
      if (file instanceof File) {
        const fileWithMeta: File & { meta?: Record<string, string> } = file;

        return {
          kind: 'new-file',
          name: file.name,
          size: file.size,
          type: file.type,
          lastModified: file.lastModified,
          meta: fileWithMeta.meta,
        };
      }

      return file;
    }),
  });

const AssessmentActivityCreateTab: FC<Props> = ({ assessmentMode }) => {
  const navigate = useNavigate();
  const assessmentId = useGetGuidParam('assessmentId');
  const { updateFiles } = useFileUpdate();
  const { insertAssessmentActivity } =
    useInsertAssessmentActivityWithLinkedItems();
  const {
    routing: { activityEditUrl, activityRegisterUrl },
  } = useAssessmentTypeConfig(assessmentMode);
  const createdActivityRef = useRef<
    | {
        assessmentId: string;
        activityId: string;
        attemptSnapshot: ReturnType<typeof createAttemptSnapshot>;
      }
    | undefined
  >(undefined);
  const currentAssessmentIdRef = useRef<string | undefined>(assessmentId);
  currentAssessmentIdRef.current = assessmentId;
  useEffect(() => {
    currentAssessmentIdRef.current = assessmentId;

    return () => {
      if (currentAssessmentIdRef.current === assessmentId) {
        currentAssessmentIdRef.current = undefined;
      }
    };
  }, [assessmentId]);
  if (createdActivityRef.current?.assessmentId !== assessmentId) {
    createdActivityRef.current = undefined;
  }

  const onSave = async (variables: AssessmentActivityFormDataFields) => {
    const saveAssessmentId = assessmentId;
    try {
      const { files } = variables;
      const attemptSnapshot = createAttemptSnapshot(
        variables,
        saveAssessmentId
      );
      let activityId = createdActivityRef.current?.activityId;
      let createdEntity: CreatedEntityDestination | undefined = activityId
        ? {
            id: activityId,
            destination: activityEditUrl(saveAssessmentId, activityId),
          }
        : undefined;
      if (
        activityId &&
        !isEqual(createdActivityRef.current?.attemptSnapshot, attemptSnapshot)
      ) {
        throw new CreatedEntityFileSaveError({
          id: activityId,
          destination: activityEditUrl(saveAssessmentId, activityId),
          cause: new Error(
            'Form values changed after the assessment activity was created'
          ),
          failureStage: 'validation',
        });
      }
      if (!activityId) {
        const result = await insertAssessmentActivity({
          ...variables,
          ParentId: saveAssessmentId,
          LinkedItemIds: variables.LinkedItemIds.map((c) => c.Id),
          CustomAttributeData: variables.CustomAttributeData || undefined,
          AssignedUser: variables.AssignedUser?.value,
          OwnerUserIds: [],
          OwnerGroupIds: [],
          Status:
            variables.Status ?? Assessment_Activity_Status_Enum.Notstarted,
          ActivityType:
            variables.ActivityType ?? Assessment_Activity_Type_Enum.Task,
        });
        activityId = result?.insertAssessmentActivityWithLinkedItems?.Id;
        if (!activityId) {
          throw new Error('Assessment Activity id is missing');
        }
        if (currentAssessmentIdRef.current === saveAssessmentId) {
          createdActivityRef.current = {
            assessmentId: saveAssessmentId,
            activityId,
            attemptSnapshot,
          };
        }
        createdEntity = {
          id: activityId,
          destination: activityEditUrl(saveAssessmentId, activityId),
        };
      }
      const saveFiles = () =>
        updateFiles({
          parentId: activityId,
          parentType: Parent_Type_Enum.AssessmentActivity,
          selectedFiles: files,
          originalFiles: [],
        });
      await saveFilesForOptionalCreatedEntity({ createdEntity, saveFiles });

      if (currentAssessmentIdRef.current !== saveAssessmentId) {
        return MUTATION_NO_OP;
      }

      return {
        savedIds: [activityId],
        createdEntity,
        successNavigation: {
          destination: activityRegisterUrl(saveAssessmentId),
        },
      };
    } catch (error) {
      if (currentAssessmentIdRef.current !== saveAssessmentId) {
        return MUTATION_NO_OP;
      }
      throw error;
    }
  };

  const onDismiss = (isSaved?: boolean) => {
    if (!isSaved && currentAssessmentIdRef.current === assessmentId) {
      navigate(activityRegisterUrl(assessmentId));
    }
  };

  return (
    <AssessmentActivityForm
      onSave={onSave}
      onDismiss={onDismiss}
      renderTemplate={(renderProps) => <PageWrapper {...renderProps} />}
    />
  );
};

export default AssessmentActivityCreateTab;
